import React, { useContext } from 'react';
import _ from 'lodash';

import { ChangeHandler } from 'src/presentation/components/pages/integrations/types';
import CompactSection from 'src/presentation/components/pages/integrations/editorComponents/CompactSection';
import { Row } from 'src/presentation/components/forms/Layout';
import {
  MaskedField,
  WrappedField,
} from 'src/presentation/components/pages/integrations/editorComponents/EditorFields';
import { DetailedSwitch } from 'src/presentation/components/forms/DetailedSwitch';
import FormContext from 'src/presentation/contexts/FormContext';

const localCurrencyDescription = (
  <span>
    Read more about Shopify currencies at:{' '}
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://help.shopify.com/en/manual/international/pricing/exchange-rates#currency-definitions-and-conversions"
    >
      Currency definitions and conversions
    </a>
    .
  </span>
);

interface ShopifyConfigurationProps {
  onChange: (attr: string) => ChangeHandler;
  maskedFields: string[];
  disableConnectionSettings?: boolean;
}

const ShopifyConfiguration: React.FC<ShopifyConfigurationProps> = ({
  onChange,
  maskedFields,
  disableConnectionSettings,
}) => {
  const { workingState } = useContext(FormContext);
  const mainSwitchHeading = (
    <>
      {_.get(workingState, 'connectionSettings.showOrderDetailsInLocalCurrency')
        ? 'Show order details in local currency'
        : 'Show order details in shop currency'}
    </>
  );
  return (
    <>
      <CompactSection
        description={`The Shopify app enriches your customer pages with customer details 
                      and order information pulled from your Shopify instance. In addition, this 
                      app allows agents to perform quick actions (like processing returns) 
                      right in Gladly.`}
      >
        <Row>
          <WrappedField
            attr="name"
            label="Name"
            placeholder="App Name"
            description="This is the name that will be displayed throughout Gladly"
            data-aid="shopify-integration-name"
            autoFocus={true}
            onChange={onChange('name')}
          />
        </Row>
        <Row>
          <WrappedField
            attr="connectionSettings.baseUrl"
            label="Shopify Base URL"
            description="This is the URL of your Shopify instance"
            placeholder="e.g. https://my-store.shopify.com"
            disabled={disableConnectionSettings}
            data-aid="shopify-base-url"
            onChange={onChange('connectionSettings.baseUrl')}
          />
        </Row>
      </CompactSection>
      <CompactSection
        header="Authentication"
        description={`Passwords and keys used to connect to other accounts are treated as secrets in Gladly.
               If you need to change an existing secret, use the clear button in the field to remove the current
               secret and enter a new one`}
      >
        <Row>
          <MaskedField
            attr="secrets.token"
            label="Shopify Access Token"
            showClearButton={maskedFields.includes('token')}
            isMasked={maskedFields.includes('token')}
            disabled={disableConnectionSettings}
            data-aid="shopify-token"
            onChange={onChange('secrets.token')}
          />
        </Row>
      </CompactSection>
      <CompactSection header="Display Options" description={localCurrencyDescription}>
        <DetailedSwitch
          attr="connectionSettings.showOrderDetailsInLocalCurrency"
          data-aid="shopify-show-order-details-in-local-currency"
          conditionalText
          enabledText={`Order amounts are displayed in the customer's local currency (the currency they saw and paid in).`}
          disabledText={`Order amounts are displayed in your store's default currency.`}
          switchHeading={mainSwitchHeading}
        />
      </CompactSection>
    </>
  );
};

function renderShopifyConfiguration(
  onChange: (attr: string) => ChangeHandler,
  maskedFields: string[],
  disableConnectionSettings?: boolean
): JSX.Element {
  return (
    <ShopifyConfiguration
      onChange={onChange}
      maskedFields={maskedFields}
      disableConnectionSettings={disableConnectionSettings}
    />
  );
}

export default renderShopifyConfiguration;
